// src/components/doctorComponents/ConsultPatientModal.jsx - COMPLETE WITH SANITIZATION
import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  searchMedicines,
  getDoctorsList,
  createMedicalRecord,
  getMedicalRecordByAppointment,
  clearMedicinesSearch,
} from "../../Redux/actions/doctorActions/doctorActions.js";
import { showToast } from "../ToastMessage.jsx";
import PrescriptionPrint from "./PrescriptionPrint.jsx";
import { useSanitizedInput } from "../../hooks/useSanitizedInput.js"; // ✅ Add import
import "./DoctorModals.css";
import { convertTo12Hour } from "../../utils/timeUtils";

// Debounce function
const debounce = (func, delay) => {
  let timeoutId;
  const debounced = (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
  debounced.cancel = () => clearTimeout(timeoutId);
  return debounced;
};


const ConsultPatientModal = ({ appointment, doctor, onClose }) => {
  const dispatch = useDispatch();
  const { medicinesSearch = [], doctorsList = [] } = useSelector(
    (state) => state.doctor || {}
  );

  // ✅ Replace form state with sanitized hooks
  const [
    chiefComplaint,
    handleChiefComplaintChange,
    resetChiefComplaint,
    setChiefComplaint,
  ] = useSanitizedInput("", "text");
  const [symptoms, handleSymptomsChange, resetSymptoms, setSymptoms] =
    useSanitizedInput("", "text");
  const [diagnosis, handleDiagnosisChange, resetDiagnosis, setDiagnosis] =
    useSanitizedInput("", "text");
  const [
    clinicalNotes,
    handleClinicalNotesChange,
    resetClinicalNotes,
    setClinicalNotes,
  ] = useSanitizedInput("", "text");
  const [
    physicalExamination,
    handlePhysicalExaminationChange,
    resetPhysicalExamination,
    setPhysicalExamination,
  ] = useSanitizedInput("", "text");
  const [
    followUpNotes,
    handleFollowUpNotesChange,
    resetFollowUpNotes,
    setFollowUpNotes,
  ] = useSanitizedInput("", "text");
  const [
    onHoldReason,
    handleOnHoldReasonChange,
    resetOnHoldReason,
    setOnHoldReason,
  ] = useSanitizedInput("", "text");
  const [
    medicineSearchQuery,
    handleMedicineSearchChange,
    resetMedicineSearch,
    setMedicineSearchQuery,
  ] = useSanitizedInput("", "text");
  const [
    customMedicineName,
    handleCustomMedicineNameChange,
    resetCustomMedicine,
    setCustomMedicineName,
  ] = useSanitizedInput("", "alphanumeric");
  const [
    referralReason,
    handleReferralReasonChange,
    resetReferralReason,
    setReferralReason,
  ] = useSanitizedInput("", "text");

  // Medicines - Enhanced for custom entries
  const [prescribedMedicines, setPrescribedMedicines] = useState([]);
  const [showCustomMedicineForm, setShowCustomMedicineForm] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);

  // Lab Tests
  const [labTests, setLabTests] = useState([]);

  // Referral
  const [isReferring, setIsReferring] = useState(false);
  const [referralDoctor, setReferralDoctor] = useState("");

  // Follow-up
  const [followUpDate, setFollowUpDate] = useState("");

  // On Hold
  const [showHoldModal, setShowHoldModal] = useState(false);

  const [currentTab, setCurrentTab] = useState("examination");
  const [submitting, setSubmitting] = useState(false);
  const [createdRecord, setCreatedRecord] = useState(null);
  const [showPrint, setShowPrint] = useState(false);
  const [saveType, setSaveType] = useState("");

  // PREFILL: Load existing medical record if appointment is on-hold
  useEffect(() => {
    if (appointment.status === "on-hold" && appointment.hasPartialRecord) {
      dispatch(getMedicalRecordByAppointment(appointment._id)).then(
        (record) => {
          if (record) {
            // ✅ Use direct setters for programmatic updates
            setChiefComplaint(record.chiefComplaint || "");
            setSymptoms(record.symptoms || "");
            setDiagnosis(record.diagnosis || "");
            setClinicalNotes(record.clinicalNotes || "");
            setPhysicalExamination(record.physicalExamination || "");
            setPrescribedMedicines(record.medicines || []);
            setLabTests(record.labTests || []);
            setIsReferring(record.referral?.isReferred || false);
            setReferralDoctor(record.referral?.referredTo || "");
            setReferralReason(record.referral?.reason || "");
            setFollowUpDate(
              record.followUpDate ? record.followUpDate.split("T")[0] : ""
            );
            setFollowUpNotes(record.followUpNotes || "");
            showToast("Previous consultation data loaded");
          }
        }
      );
    }
  }, [
    appointment._id,
    appointment.status,
    appointment.hasPartialRecord,
    dispatch,
    setChiefComplaint,
    setSymptoms,
    setDiagnosis,
    setClinicalNotes,
    setPhysicalExamination,
    setReferralReason,
    setFollowUpNotes,
  ]);

  useEffect(() => {
    dispatch(getDoctorsList());
  }, [dispatch]);

  const doSearch = useCallback(
    debounce((query) => {
      if (query && query.trim().length >= 2) {
        dispatch(searchMedicines(query.trim()));
      }
    }, 300),
    [dispatch]
  );

  useEffect(() => {
    if (medicineSearchQuery.trim().length >= 2) {
      doSearch(medicineSearchQuery);
      setShowSearchDropdown(true);
    } else {
      setShowSearchDropdown(false);
    }

    return () => {
      doSearch.cancel();
    };
  }, [medicineSearchQuery, doSearch]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".position-relative")) {
        setShowSearchDropdown(false);
      }
    };

    if (showSearchDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSearchDropdown]);

  // Add medicine from database (with or without stock)
  const addMedicine = (medicine) => {
    const exists = prescribedMedicines.find(
      (m) => m.medicineName.toLowerCase() === medicine.name.toLowerCase()
    );
    if (exists) {
      showToast("Medicine already added");
      return;
    }

    setPrescribedMedicines([
      ...prescribedMedicines,
      {
        medicine: medicine._id || null,
        medicineName: medicine.name,
        company: medicine.company || "",
        currentStock: medicine.stock || 0,
        dosage: "",
        frequency: "",
        duration: "",
        instructions: "",
        route: "",
        isCustom: false,
      },
    ]);
    resetMedicineSearch(); // ✅ Use hook's reset
    setShowSearchDropdown(false);
    dispatch(clearMedicinesSearch());
    showToast(`${medicine.name} added to prescription`);
  };

  // Add custom medicine (not in database)
  const addCustomMedicine = () => {
    if (!customMedicineName.trim()) {
      showToast("Please enter medicine name");
      return;
    }

    // ✅ Check length after sanitization
    if (customMedicineName.length > 200) {
      showToast("Medicine name is too long (max 200 characters)");
      return;
    }

    const exists = prescribedMedicines.find(
      (m) => m.medicineName.toLowerCase() === customMedicineName.toLowerCase()
    );
    if (exists) {
      showToast("Medicine already added");
      return;
    }

    setPrescribedMedicines([
      ...prescribedMedicines,
      {
        medicine: null,
        medicineName: customMedicineName.trim(),
        company: "",
        currentStock: 0,
        dosage: "",
        frequency: "",
        duration: "",
        instructions: "",
        route: "",
        isCustom: true,
      },
    ]);

    resetCustomMedicine(); // ✅ Use hook's reset
    setShowCustomMedicineForm(false);
    showToast("Custom medicine added");
  };

  const updateMedicine = (index, field, value) => {
    const updated = [...prescribedMedicines];

    // ✅ Sanitize medicine field updates
    if (
      field === "dosage" ||
      field === "frequency" ||
      field === "duration" ||
      field === "route"
    ) {
      value = value.replace(/[<>]/g, "").substring(0, 100);
    } else if (field === "instructions") {
      value = value.replace(/[<>]/g, "").substring(0, 500);
    }

    updated[index][field] = value;
    setPrescribedMedicines(updated);
  };

  const removeMedicine = (index) => {
    setPrescribedMedicines(prescribedMedicines.filter((_, i) => i !== index));
  };

  const addLabTest = () => {
    setLabTests([
      ...labTests,
      {
        testName: "",
        urgent: false,
        resultRemarks: "",
      },
    ]);
  };
const handlePrintLabTests = async () => {
  if (labTests.length === 0) {
    showToast("No lab tests to print");
    return;
  }

  try {
    // Import the print function
    const { printLabTestSlip } = await import('./printLabTestSlip');
    
    const labTestData = {
      appointment,
      labTests,
      patientInfo: {
        name: appointment.pName,
        mrCode: appointment.mrCode,
        age: appointment.age,
        contact: appointment.contact,
      },
      doctorInfo: {
        name: doctor,
        department: appointment.department,
      }
    };
    
    await printLabTestSlip(labTestData);
    showToast("Lab test slip printed successfully");
  } catch (error) {
    console.error("Print lab tests error:", error);
    showToast("Failed to print lab tests");
  }
};
  const updateLabTest = (index, field, value) => {
    const updated = [...labTests];

    // ✅ Sanitize lab test field updates
    if (field === "testName") {
      value = value.replace(/[<>]/g, "").substring(0, 200);
      // ✅ NEW: Auto-fill testName when testType changes
      if (field === "testType") {
        updated[index].testName = value;
      }
    } else if (field === "resultRemarks") {
      value = value.replace(/[<>]/g, "").substring(0, 1000);
    }

    updated[index][field] = value;
    setLabTests(updated);
  };

  const removeLabTest = (index) => {
    setLabTests(labTests.filter((_, i) => i !== index));
  };

  const handleSubmit = async (type) => {
    setSaveType(type);

    // ✅ Validate length limits
    if (chiefComplaint.length > 500) {
      showToast("Chief complaint is too long (max 500 characters)");
      return;
    }

    if (diagnosis.length > 500) {
      showToast("Diagnosis is too long (max 500 characters)");
      return;
    }

    if (symptoms.length > 1000) {
      showToast("HOPC  is too long (max 1000 characters)");
      return;
    }

    if (clinicalNotes.length > 2000) {
      showToast("Clinical notes are too long (max 2000 characters)");
      return;
    }

    if (physicalExamination.length > 1000) {
      showToast("Physical examination is too long (max 1000 characters)");
      return;
    }

    if (followUpNotes.length > 500) {
      showToast("Follow-up notes are too long (max 500 characters)");
      return;
    }

    if (!chiefComplaint.trim() || !diagnosis.trim()) {
      showToast("Chief complaint and diagnosis are required");
      return;
    }

    // For "complete", validate medicines if prescribed
    if (type === "complete" && prescribedMedicines.length > 0) {
      // ✅ Check medicines array size
      if (prescribedMedicines.length > 50) {
        showToast("Too many medicines (max 50)");
        return;
      }

      for (let med of prescribedMedicines) {
        if (!med.dosage || !med.frequency || !med.duration) {
          showToast(`Please complete all details for ${med.medicineName}`);
          return;
        }
      }
    }

    // ✅ Validate lab tests
    if (labTests.length > 30) {
      showToast("Too many lab tests (max 30)");
      return;
    }

    for (let test of labTests) {
      if (!test.testName.trim()) {
        showToast("Please provide test names for all lab tests");
        return;
      }
      if (test.testName.length > 200) {
        showToast("Lab test name is too long (max 200 characters)");
        return;
      }
    }

    const referralData = isReferring
      ? {
          isReferred: true,
          referredTo: referralDoctor,
          reason: referralReason,
        }
      : { isReferred: false };

    const recordData = {
      appointmentId: appointment._id,
      patientId: appointment.patientId?._id || null,
      mrCode: appointment.mrCode,
      chiefComplaint,
      symptoms,
      diagnosis,
      clinicalNotes,
      physicalExamination,
      medicines: prescribedMedicines,
      labTests,
      referral: referralData,
      followUpDate: followUpDate || null,
      followUpNotes,
      saveType: type,
      onHoldReason: type === "hold" ? onHoldReason : null,
    };

    try {
      setSubmitting(true);
      const record = await dispatch(createMedicalRecord(recordData));
      setCreatedRecord(record);

      if (type === "hold") {
        showToast("Appointment put on hold successfully!");
        onClose();
      } else {
        showToast("Medical record created successfully!");
        setShowPrint(true);
      }
    } catch (error) {
      showToast("Failed to save medical record");
    } finally {
      setSubmitting(false);
    }
  };

  if (showPrint && createdRecord) {
    return (
      <PrescriptionPrint
        medicalRecord={createdRecord}
        appointment={appointment}
        onClose={() => {
          setShowPrint(false);
          onClose();
        }}
      />
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-container modal-xl">
        <div className="modal-header-custom">
          <h4>
            <i className="bi bi-clipboard2-pulse me-2"></i>
            {appointment.status === "on-hold"
              ? "Continue Consultation"
              : "Consult Patient"}{" "}
            - {appointment.pName}
            {appointment.status === "on-hold" && (
              <span className="badge bg-warning ms-2">On Hold</span>
            )}
          </h4>
          <button className="btn-close-custom" onClick={onClose}>
            <i className="bi bi-x-lg">Close</i>
          </button>
        </div>

        {/* Patient Info Banner with Vitals */}
        <div className="patient-info-banner">
          <div className="row">
            <div className="col-md-3">
              <strong>MR Code:</strong>{" "}
              <code>{appointment.mrCode || "N/A"}</code>
            </div>
            <div className="col-md-3">
              <strong>Age:</strong> {appointment.age || "N/A"}
            </div>
            <div className="col-md-3">
              <strong>Contact:</strong> {appointment.contact || "N/A"}
            </div>
            <div className="col-md-3">
              <strong>Appt #:</strong> {appointment.appointmentNumber}
            </div>
          </div>
          {appointment.vitals &&
            (appointment.vitals.bp || appointment.vitals.weight) && (
              <div className="row mt-2">
                <div className="col-12">
                  <div
                    className="alert alert-info mb-0"
                    style={{ padding: "0.5rem 1rem" }}
                  >
                    <strong>📊 Vitals:</strong>{" "}
                    {appointment.vitals.bp && `BP: ${appointment.vitals.bp}`}
                    {appointment.vitals.weight &&
                      ` | Weight: ${appointment.vitals.weight}kg`}
                    {appointment.vitals.height &&
                      ` | Height: ${appointment.vitals.height}cm`}
                    {appointment.vitals.bmi &&
                      ` | BMI: ${appointment.vitals.bmi}`}
                    {appointment.vitals.spo2 &&
                      ` | SPO2: ${appointment.vitals.spo2}%`}
                    {appointment.vitals.temperature &&
                      ` | Temp: ${appointment.vitals.temperature}°F`}
                  </div>
                </div>
              </div>
            )}
        </div>

        {/* Tabs */}
        <ul className="nav nav-tabs mt-3">
          <li className="nav-item">
            <button
              className={`nav-link ${
                currentTab === "examination" ? "active" : ""
              }`}
              onClick={() => setCurrentTab("examination")}
            >
              Examination
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${
                currentTab === "prescription" ? "active" : ""
              }`}
              onClick={() => setCurrentTab("prescription")}
            >
              Prescription ({prescribedMedicines.length})
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${
                currentTab === "labtests" ? "active" : ""
              }`}
              onClick={() => setCurrentTab("labtests")}
            >
              Lab Tests ({labTests.length})
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${
                currentTab === "referral" ? "active" : ""
              }`}
              onClick={() => setCurrentTab("referral")}
            >
              Referral
            </button>
          </li>
        </ul>

        <div className="modal-body-custom">
          {/* Examination Tab */}
          {currentTab === "examination" && (
            <div>
              <div className="mb-3">
                <label className="form-label fw-bold">
                  Chief Complaint <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Main reason for visit"
                  value={chiefComplaint}
                  onChange={handleChiefComplaintChange} // ✅ Use sanitized handler
                  maxLength={500} // ✅ Add max length
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">HOPC (History of Present Complaint)</label>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder="Describe symptoms"
                  value={symptoms}
                  onChange={handleSymptomsChange} // ✅ Use sanitized handler
                  maxLength={1000} // ✅ Add max length
                ></textarea>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">
                  Physical Examination
                </label>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder="Physical examination findings"
                  value={physicalExamination}
                  onChange={handlePhysicalExaminationChange} // ✅ Use sanitized handler
                  maxLength={1000} // ✅ Add max length
                ></textarea>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">
                  Diagnosis <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Final diagnosis"
                  value={diagnosis}
                  onChange={handleDiagnosisChange} // ✅ Use sanitized handler
                  maxLength={500} // ✅ Add max length
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Clinical Notes</label>
                <textarea
                  className="form-control"
                  rows="4"
                  placeholder="Additional clinical notes"
                  value={clinicalNotes}
                  onChange={handleClinicalNotesChange} // ✅ Use sanitized handler
                  maxLength={2000} // ✅ Add max length
                ></textarea>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold">Follow-up Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={followUpDate}
                    onChange={(e) => setFollowUpDate(e.target.value)}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold">Follow-up Notes</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Follow-up instructions"
                    value={followUpNotes}
                    onChange={handleFollowUpNotesChange} // ✅ Use sanitized handler
                    maxLength={500} // ✅ Add max length
                  />
                </div>
              </div>
            </div>
          )}

          {/* Prescription Tab - ENHANCED */}
          {currentTab === "prescription" && (
            <div>
              <div className="mb-3">
                <label className="form-label fw-bold">
                  Search Medicine Database
                </label>
                <div className="position-relative">
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Type medicine name to search..."
                      value={medicineSearchQuery}
                      onChange={handleMedicineSearchChange} // ✅ Use sanitized handler
                      maxLength={100} // ✅ Add max length
                    />
                    <button
                      className="btn btn-outline-primary"
                      onClick={() =>
                        setShowCustomMedicineForm(!showCustomMedicineForm)
                      }
                    >
                      <i className="bi bi-plus-circle me-1"></i>
                      Add Custom
                    </button>
                  </div>

                  {/* Medicine Search Results */}
                  {showSearchDropdown && medicineSearchQuery.length >= 2 && (
                    <div
                      className="medicine-dropdown"
                      style={{
                        zIndex: 1000,
                        maxHeight: "300px",
                        overflowY: "auto",
                      }}
                    >
                      {medicinesSearch && medicinesSearch.length > 0 ? (
                        medicinesSearch.map((med) => (
                          <div
                            key={med._id}
                            className="medicine-item p-3 border-bottom bg-white"
                            onClick={() => addMedicine(med)}
                            style={{ cursor: "pointer" }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.backgroundColor =
                                "#f8f9fa")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.backgroundColor = "white")
                            }
                          >
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <strong>{med.name}</strong>
                                {med.company && (
                                  <span className="text-muted">
                                    {" "}
                                    - {med.company}
                                  </span>
                                )}
                                <br />
                                <small className="text-muted">
                                  Stock: {med.stock || 0} | Price: Rs.{" "}
                                  {med.price}
                                </small>
                              </div>
                              {med.stock === 0 && (
                                <span className="badge bg-danger">
                                  Out of Stock
                                </span>
                              )}
                              {med.stock > 0 &&
                                med.stock <= (med.lowStockLimit || 10) && (
                                  <span className="badge bg-warning">
                                    Low Stock
                                  </span>
                                )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-3 bg-white border">
                          <small className="text-muted">
                            <i className="bi bi-search me-2"></i>
                            Searching...
                          </small>
                        </div>
                      )}
                    </div>
                  )}

                  {medicineSearchQuery.length >= 2 &&
                    (!medicinesSearch || medicinesSearch.length === 0) && (
                      <div className="alert alert-info mt-2 mb-0">
                        <i className="bi bi-info-circle me-2"></i>
                        No medicines found. You can add it as a custom medicine
                        using the "Add Custom" button.
                      </div>
                    )}
                </div>
              </div>

              {/* Custom Medicine Form */}
              {showCustomMedicineForm && (
                <div className="card mb-3 border-primary">
                  <div className="card-body">
                    <h6 className="card-title">
                      <i className="bi bi-pencil-square me-2"></i>
                      Add Custom Medicine
                    </h6>
                    <div className="row">
                      <div className="col-md-8 mb-2">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter medicine name"
                          value={customMedicineName}
                          onChange={handleCustomMedicineNameChange} // ✅ Use sanitized handler
                          onKeyPress={(e) =>
                            e.key === "Enter" && addCustomMedicine()
                          }
                          maxLength={200} // ✅ Add max length
                        />
                      </div>
                      <div className="col-md-2 mb-2 d-flex align-items-center gap-2">
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={addCustomMedicine}
                        >
                          Add
                        </button>

                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => {
                            setShowCustomMedicineForm(false);
                            resetCustomMedicine();
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                    <small className="text-muted">
                      <i className="bi bi-info-circle me-1"></i>
                      Custom medicines are not tracked in inventory
                    </small>
                  </div>
                </div>
              )}

              {/* Prescribed Medicines List */}
              <div className="prescribed-medicines">
                {prescribedMedicines.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <i className="bi bi-capsule display-4"></i>
                    <p className="mt-2">No medicines prescribed yet</p>
                  </div>
                ) : (
                  prescribedMedicines.map((med, idx) => (
                    <div key={idx} className="medicine-card">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <h6 className="mb-0">
                            {med.medicineName}
                            {med.isCustom && (
                              <span className="badge bg-info ms-2">Custom</span>
                            )}
                            {!med.isCustom && med.currentStock === 0 && (
                              <span className="badge bg-danger ms-2">
                                Out of Stock
                              </span>
                            )}
                            {!med.isCustom &&
                              med.currentStock > 0 &&
                              med.currentStock <= 10 && (
                                <span className="badge bg-warning ms-2">
                                  Low Stock: {med.currentStock}
                                </span>
                              )}
                          </h6>
                          {med.company && (
                            <small className="text-muted">{med.company}</small>
                          )}
                        </div>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => removeMedicine(idx)}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>

                      <div className="row">
                        <div className="col-md-3 mb-2">
                          <label className="form-label small">
                            Dosage <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="e.g., 500mg, 1 tab"
                            value={med.dosage}
                            onChange={(e) =>
                              updateMedicine(idx, "dosage", e.target.value)
                            }
                            maxLength={100} // ✅ Add max length
                          />
                        </div>
                        <div className="col-md-3 mb-2">
                          <label className="form-label small">
                            Frequency <span className="text-danger">*</span>
                          </label>
                          <select
                            className="form-select form-select-sm"
                            value={med.frequency}
                            onChange={(e) =>
                              updateMedicine(idx, "frequency", e.target.value)
                            }
                          >
                            <option value="">Select</option>
                            <option value="Once daily">Once daily</option>
                            <option value="Twice daily">Twice daily</option>
                            <option value="Three times daily">
                              Three times daily
                            </option>
                            <option value="Four times daily">
                              Four times daily
                            </option>
                            <option value="Every 4 hours">Every 4 hours</option>
                            <option value="Every 6 hours">Every 6 hours</option>
                            <option value="Every 8 hours">Every 8 hours</option>
                            <option value="As needed">As needed (PRN)</option>
                            <option value="At bedtime">At bedtime</option>
                          </select>
                        </div>
                        <div className="col-md-3 mb-2">
                          <label className="form-label small">
                            Duration <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="e.g., 7 days, 2 weeks"
                            value={med.duration}
                            onChange={(e) =>
                              updateMedicine(idx, "duration", e.target.value)
                            }
                            maxLength={100} // ✅ Add max length
                          />
                        </div>
                        <div className="col-md-3 mb-2">
                          <label className="form-label small">Route</label>
                          <select
                            className="form-select form-select-sm"
                            value={med.route}
                            onChange={(e) => updateMedicine(idx, "route", e.target.value)}
                          >
                            <option value="">Select</option>
                            <option value="Oral">Oral</option>
                            <option value="IV">IV (Intravenous)</option>
                            <option value="IM">IM (Intramuscular)</option>
                            <option value="SC">SC (Subcutaneous)</option>
                            <option value="Topical">Topical</option>
                            <option value="Rectal">Rectal</option>
                            <option value="Inhalation">Inhalation</option>
                            <option value="Sublingual">Sublingual</option>
                          </select>
                        </div>
                        <div className="col-md-12 mb-2">
                          <label className="form-label small">
                            Additional Instructions
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="e.g., Avoid dairy, Take with water"
                            value={med.instructions}
                            onChange={(e) =>
                              updateMedicine(
                                idx,
                                "instructions",
                                e.target.value
                              )
                            }
                            maxLength={500} // ✅ Add max length
                          />
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Lab Tests Tab */}
          {currentTab === "labtests" && (
            <div>
              <div className="mb-3">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={addLabTest}
                  style={{
                    width: "auto",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  <i className="bi bi-plus-lg me-1"></i>
                  Add Lab Test
                </button>
                {/* ✅ NEW: Print Lab Tests Button */}
                {labTests.length > 0 && (
                  <button
                    className="btn btn-outline-primary btn-sm ms-2"
                    onClick={() => handlePrintLabTests()}
                  >
                    <i className="bi bi-printer me-1"></i>
                    Print Lab Tests
                  </button>
                )}
              </div>

              {labTests.length === 0 ? (
                <p className="text-muted text-center">No lab tests added</p>
              ) : (
                labTests.map((test, idx) => (
                  <div key={idx} className="lab-test-card">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h6 className="mb-0">Test #{idx + 1}</h6>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => removeLabTest(idx)}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                    <div className="row">
                      <div className="col-md-8 mb-2">
                        <label className="form-label small">Test Name</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Specific test name"
                          value={test.testName}
                          onChange={(e) =>
                            updateLabTest(idx, "testName", e.target.value)
                          }
                          maxLength={200} // ✅ Add max length
                        />
                      </div>
                      <div className="col-md-12">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={test.urgent}
                            onChange={(e) =>
                              updateLabTest(idx, "urgent", e.target.checked)
                            }
                          />
                          <label className="form-check-label small">
                            Mark as Urgent
                          </label>
                        </div>
                      </div>
                      <div className="col-md-12 mt-2">
                        <label className="form-label small">
                          <i className="bi bi-clipboard-check me-1"></i>
                          Lab Result Remarks (Optional)
                        </label>
                        <textarea
                          className="form-control form-control-sm"
                          rows="2"
                          placeholder="Enter lab result findings here..."
                          value={test.resultRemarks || ""}
                          onChange={(e) =>
                            updateLabTest(idx, "resultRemarks", e.target.value)
                          }
                          maxLength={1000} // ✅ Add max length
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Referral Tab */}
          {currentTab === "referral" && (
            <div>
              <div className="mb-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={isReferring}
                    onChange={(e) => setIsReferring(e.target.checked)}
                  />
                  <label className="form-check-label fw-bold">
                    Refer to Another Doctor
                  </label>
                </div>
              </div>

              {isReferring && (
                <>
                  <div className="mb-3">
                    <label className="form-label fw-bold">Select Doctor</label>
                    <select
                      className="form-select"
                      value={referralDoctor}
                      onChange={(e) => setReferralDoctor(e.target.value)}
                    >
                      <option value="">Select a doctor</option>
                      {doctorsList.map((doc) => (
                        <option key={doc._id} value={doc._id}>
                          Dr. {doc.name} - {doc.specialization} (
                          {doc.department})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      Referral Reason
                    </label>
                    <textarea
                      className="form-control"
                      rows="3"
                      placeholder="Reason for referral"
                      value={referralReason}
                      onChange={handleReferralReasonChange} // ✅ Use sanitized handler
                      maxLength={500} // ✅ Add max length
                    ></textarea>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer with Hold and Complete buttons */}
        <div className="modal-footer-custom">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>

          <div className="d-flex gap-2">
            <button
              className="btn btn-warning"
              onClick={() => setShowHoldModal(true)}
              disabled={submitting}
            >
              <i className="bi bi-hourglass-split me-1"></i>
              Put on Hold
            </button>

            <button
              className="btn btn-success"
              onClick={() => handleSubmit("complete")}
              disabled={submitting}
            >
              <i className="bi bi-check-circle me-1"></i>
              {submitting && saveType === "complete"
                ? "Saving..."
                : "Complete & Print Prescription"}
            </button>
          </div>
        </div>
      </div>

      {/* Hold Reason Modal */}
      {showHoldModal && (
        <div
          className="modal-overlay"
          style={{ zIndex: 10000 }}
          onClick={(e) => {
            if (e.target.className === "modal-overlay") {
              setShowHoldModal(false);
            }
          }}
        >
          <div
            className="modal-container"
            style={{
              maxWidth: "500px",
              zIndex: 10001,
            }}
          >
            <div className="modal-header-custom">
              <h5>
                <i className="bi bi-hourglass-split me-2"></i>
                Put Appointment on Hold
              </h5>
              <button
                className="btn-close-custom"
                onClick={() => setShowHoldModal(false)}
              >
                <i className="bi bi-x-lg">Close</i>
              </button>
            </div>

            <div className="modal-body-custom">
              <div className="alert alert-warning">
                <i className="bi bi-info-circle me-2"></i>
                This will save your progress and mark the appointment as "On
                Hold". You can continue later.
              </div>

              <label className="form-label fw-bold">
                Reason for Hold <span className="text-danger">*</span>
              </label>
              <textarea
                className="form-control"
                rows="3"
                placeholder="e.g., Waiting for lab results, Need specialist consultation"
                value={onHoldReason}
                onChange={handleOnHoldReasonChange} // ✅ Use sanitized handler
                autoFocus
                maxLength={500} // ✅ Add max length
              ></textarea>
            </div>

            <div className="modal-footer-custom">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowHoldModal(false);
                  resetOnHoldReason(); // ✅ Use hook's reset
                }}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                className="btn btn-warning"
                onClick={() => {
                  if (!onHoldReason.trim()) {
                    showToast("Please provide a reason for putting on hold");
                    return;
                  }
                  if (onHoldReason.length > 500) {
                    showToast("Reason is too long (max 500 characters)");
                    return;
                  }
                  setShowHoldModal(false);
                  handleSubmit("hold");
                }}
                disabled={!onHoldReason.trim() || submitting}
              >
                <i className="bi bi-hourglass-split me-1"></i>
                {submitting && saveType === "hold"
                  ? "Saving..."
                  : "Save & Put on Hold"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultPatientModal;
